import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import StoreMallDirectoryIcon from "@mui/icons-material/StoreMallDirectory";
import ForkliftIcon from "@mui/icons-material/Agriculture";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import { selectUser } from "../../../features/userSlice";
import { Card, PageHeader, PageShell, SectionHeader } from "../../../components/UI/Page";
import { EmptyState, LoadingState } from "../../../components/UI/States";
import { useLocations } from "../../../components/Fleet/Forklifts/Utilities/useLocations";
import {
  BackLink,
  Fact,
  Notice,
  Pill,
  ServiceChip,
  ServiceDialog,
  serviceSummary,
} from "../../../components/Fleet/FleetParts";
import {
  AddForkliftDialog,
  ConfirmDialog,
  EditForkliftDialog,
  ErrorLine,
} from "../../../components/Fleet/ForkliftDialogs";
import {
  HISTORY_DAYS,
  checkinTone,
  formatDate,
  formatDateTime,
  relativeDay,
} from "../../../components/Fleet/fleetFormat";
import {
  deleteForklift,
  deleteLocation,
  fetchLocation,
  setForkliftService,
  updateLocation,
} from "../fleetApi";
import { lastCheckIn } from "../ForkliftList";

// One branch location from Location Manager: its contacts, shifts and alert
// thresholds (each card edits in place and saves only its own fields), and
// its forklifts, which can be added, edited, taken out of service or
// deleted right here.

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const CONTACT_KEYS = [
  "managerName",
  "managerPhone",
  "managerEmail",
  "generalName",
  "generalPhone",
  "generalEmail",
];

const draftFor = (section, location) => {
  if (section === "contacts") {
    return Object.fromEntries(CONTACT_KEYS.map((key) => [key, location[key] || ""]));
  }
  if (section === "alerts") {
    return { emailAlert: location.emailAlert || "", textAlert: location.textAlert || "" };
  }
  return { shifts: location.shifts.map((shift) => ({ ...shift })) };
};

/**
 * A card that reads as plain text until Edit is clicked. Only one card is
 * edited at a time; Save sends just that card's fields.
 */
const EditableCard = ({
  title,
  description,
  editing,
  canEdit,
  onEdit,
  onCancel,
  onSave,
  saving,
  error,
  className = "",
  children,
}) => (
  <Card className={className}>
    <SectionHeader
      title={title}
      description={description}
      actions={
        !editing && (
          <button
            type="button"
            className="app-btn app-btn-sm app-btn-secondary"
            onClick={onEdit}
            disabled={!canEdit}
          >
            <EditRoundedIcon sx={{ fontSize: 16 }} /> Edit
          </button>
        )
      }
    />
    {children}
    {editing && (
      <>
        <ErrorLine>{error}</ErrorLine>
        <div className="mt-4 flex flex-col-reverse gap-2 border-t app-divider pt-4 sm:flex-row sm:justify-end">
          <Button color="inherit" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </>
    )}
  </Card>
);

const ContactView = ({ title, name, phone, email }) => (
  <div className="app-surface min-w-0 rounded-xl border p-3">
    <p className="app-eyebrow">{title}</p>
    {name ? (
      <p className="mt-0.5 truncate font-semibold">{name}</p>
    ) : (
      <p className="app-muted mt-0.5 text-sm italic">Not set</p>
    )}
    {phone && (
      <a href={`tel:${phone}`} className="app-link mt-0.5 flex items-center gap-1.5 text-sm tabular-nums">
        <PhoneRoundedIcon sx={{ fontSize: 15 }} />
        {phone}
      </a>
    )}
    {email && (
      <a href={`mailto:${email}`} className="app-link mt-0.5 flex items-center gap-1.5 truncate text-sm">
        <EmailRoundedIcon sx={{ fontSize: 15 }} />
        <span className="truncate">{email}</span>
      </a>
    )}
  </div>
);

const ContactFields = ({ title, prefix, draft, onChange }) => (
  <div className="space-y-3">
    <p className="app-eyebrow">{title}</p>
    <TextField
      fullWidth
      size="small"
      label="Name"
      value={draft[`${prefix}Name`]}
      onChange={(e) => onChange(`${prefix}Name`, e.target.value)}
    />
    <TextField
      fullWidth
      size="small"
      label="Phone"
      type="tel"
      value={draft[`${prefix}Phone`]}
      onChange={(e) => onChange(`${prefix}Phone`, e.target.value)}
    />
    <TextField
      fullWidth
      size="small"
      label="Email"
      type="email"
      value={draft[`${prefix}Email`]}
      onChange={(e) => onChange(`${prefix}Email`, e.target.value)}
    />
  </div>
);

const ShiftsEditor = ({ shifts, onChange }) => {
  const updateShift = (index, field, value) =>
    onChange(shifts.map((shift, i) => (i === index ? { ...shift, [field]: value } : shift)));

  // Ids are what check-ins store, so existing ones never change; a new
  // shift takes the next number.
  const addShift = () => {
    const next = Math.max(0, ...shifts.map((s) => Number(s.id)).filter(Number.isFinite)) + 1;
    onChange([
      ...shifts,
      { id: String(next), name: "", managerName: "", managerEmail: "", managerPhone: "" },
    ]);
  };

  return (
    <div className="space-y-3">
      {shifts.map((shift, index) => (
        <div key={shift.id} className="app-surface rounded-xl border p-3">
          <div className="flex items-center gap-2">
            <TextField
              fullWidth
              size="small"
              label="Shift name"
              placeholder={`Shift ${index + 1}`}
              value={shift.name}
              onChange={(e) => updateShift(index, "name", e.target.value)}
            />
            <button
              type="button"
              className="app-icon-btn shrink-0 text-rose-600 dark:text-rose-400"
              aria-label={`Remove ${shift.name || "shift"}`}
              title="Remove shift"
              onClick={() => onChange(shifts.filter((_, i) => i !== index))}
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </button>
          </div>
          <div className="mt-3 space-y-3">
            <TextField
              fullWidth
              size="small"
              label="Supervisor"
              value={shift.managerName || ""}
              onChange={(e) => updateShift(index, "managerName", e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Supervisor email"
              type="email"
              value={shift.managerEmail || ""}
              onChange={(e) => updateShift(index, "managerEmail", e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Supervisor phone"
              type="tel"
              value={shift.managerPhone || ""}
              onChange={(e) => updateShift(index, "managerPhone", e.target.value)}
            />
          </div>
        </div>
      ))}
      <button type="button" className="app-btn app-btn-sm app-btn-secondary" onClick={addShift}>
        <AddRoundedIcon sx={{ fontSize: 16 }} /> Add shift
      </button>
    </div>
  );
};

const LocationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { locations } = useLocations();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Settings cards: which one is open, and its unsaved values.
  const [editing, setEditing] = useState(null); // "contacts" | "shifts" | "alerts"
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Dialogs: { type: "add" | "edit" | "service" | "delete" | "remove", forklift? }
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [moveTo, setMoveTo] = useState("");

  const load = useCallback(async () => {
    if (!user?.id) return;
    setError(null);
    try {
      setData(await fetchLocation(user.id, id));
    } catch (e) {
      setError(e.message);
    }
  }, [user?.id, id]);

  useEffect(() => {
    setData(null);
    setEditing(null);
    load();
  }, [load]);

  const locationNames = useMemo(
    () => locations.map((l) => l.location).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [locations]
  );

  const startEdit = (section) => {
    setDraft(draftFor(section, data.location));
    setSaveError(null);
    setEditing(section);
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft(null);
    setSaveError(null);
  };

  const saveEdit = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const changes =
        editing === "shifts"
          ? {
              shifts: draft.shifts.map((shift, index) => ({
                ...shift,
                name: shift.name.trim() || `Shift ${index + 1}`,
              })),
            }
          : draft;
      await updateLocation(user.id, id, changes);
      await load();
      cancelEdit();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const openDialog = (type, forklift = null) => {
    setActionError(null);
    setMoveTo("");
    setDialog({ type, forklift });
  };

  // Keeps the forklift so the dialog doesn't go blank while it closes.
  const closeDialog = () => {
    setDialog((current) => current && { ...current, type: null });
    setActionError(null);
  };

  const run = async (action) => {
    setBusy(true);
    setActionError(null);
    try {
      await action();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <PageShell size="2xl">
        <BackLink to="/fleet/forks/locationmanager">Location Manager</BackLink>
        {error ? (
          <div className="app-card rounded-2xl border">
            <EmptyState icon={StoreMallDirectoryIcon} title="Couldn't open this location." description={error} />
          </div>
        ) : (
          <LoadingState label="Loading location…" />
        )}
      </PageShell>
    );
  }

  const { location, forklifts } = data;
  const outOfService = forklifts.filter((f) => !f.inService).length;
  const moveOptions = locationNames.filter(
    (name) => name.toLowerCase() !== location.location.toLowerCase()
  );
  const target = dialog?.forklift;
  // The unit's page links back here instead of to the Forklifts list.
  const backState = { backTo: { to: `/fleet/forks/locationmanager/${id}`, label: location.location } };
  const openForklift = (f) => navigate(`/fleet/forklifts/${f.id}`, { state: backState });
  const cardProps = (section) => ({
    editing: editing === section,
    canEdit: !editing,
    onEdit: () => startEdit(section),
    onCancel: cancelEdit,
    onSave: saveEdit,
    saving,
    error: saveError,
  });

  return (
    <PageShell size="2xl">
      <AddForkliftDialog
        open={dialog?.type === "add"}
        onClose={closeDialog}
        onAdded={() => {
          closeDialog();
          load();
        }}
        locations={locationNames}
        userId={user?.id}
        defaultBranch={location.location}
      />
      <EditForkliftDialog
        forklift={target}
        open={dialog?.type === "edit"}
        onClose={closeDialog}
        onSaved={() => {
          closeDialog();
          load();
        }}
        locations={locationNames}
        userId={user?.id}
      />
      {/* Out of service blocks check-ins on the branch kiosk until it's
          put back; the reason is logged in the unit's history. */}
      <ServiceDialog
        open={dialog?.type === "service"}
        assetName={`unit #${target?.unit}`}
        inService={target?.inService}
        onClose={closeDialog}
        onSubmit={async (change) => {
          await setForkliftService(user.id, target.id, change);
          closeDialog();
          await load();
        }}
      />
      <ConfirmDialog
        open={dialog?.type === "delete"}
        title={`Delete unit #${target?.unit}?`}
        body="It's removed from the fleet, the branch kiosk and the mobile app. Its check-in history is kept and still shows in the Inspection Log."
        confirmLabel="Delete forklift"
        danger
        busy={busy}
        error={actionError}
        onClose={closeDialog}
        onConfirm={() =>
          run(async () => {
            await deleteForklift(user.id, target.id);
            closeDialog();
            await load();
          })
        }
      />
      <ConfirmDialog
        open={dialog?.type === "remove"}
        title={`Remove ${location.location}?`}
        body={`It comes off Location Manager and the branch lists, and its kiosk link stops listing forklifts. Past check-ins stay in the Inspection Log.${
          forklifts.length
            ? ` Its ${plural(forklifts.length, "forklift")} will move to the location you pick.`
            : ""
        }`}
        confirmLabel="Remove location"
        danger
        busy={busy}
        disabled={forklifts.length > 0 && !moveTo}
        error={actionError}
        onClose={closeDialog}
        onConfirm={() =>
          run(async () => {
            await deleteLocation(user.id, id, { moveTo: forklifts.length ? moveTo : undefined });
            navigate("/fleet/forks/locationmanager", { replace: true });
          })
        }
      >
        {forklifts.length > 0 && (
          <FormControl fullWidth size="small" sx={{ mt: 2.5 }}>
            <InputLabel id="move-forklifts-to">Move forklifts to</InputLabel>
            <Select
              labelId="move-forklifts-to"
              label="Move forklifts to"
              value={moveTo}
              onChange={(e) => setMoveTo(e.target.value)}
            >
              {moveOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </ConfirmDialog>

      <BackLink to="/fleet/forks/locationmanager">Location Manager</BackLink>
      <PageHeader
        icon={StoreMallDirectoryIcon}
        tone="teal"
        eyebrow="Location"
        title={location.location}
        actions={
          <>
            <Link
              to={`/fleet/forks/dashboard/${encodeURIComponent(location.location)}`}
              target="_blank"
              className="app-btn app-btn-secondary"
            >
              Open kiosk <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
            </Link>
            <button
              type="button"
              className="app-icon-btn text-rose-600 dark:text-rose-400"
              onClick={() => openDialog("remove")}
              aria-label="Remove location"
              title="Remove location"
            >
              <DeleteOutlineRoundedIcon />
            </button>
          </>
        }
      >
        <div className="mt-3 flex flex-wrap gap-2">
          <Pill tone="sky">{plural(forklifts.length, "forklift")}</Pill>
          {outOfService > 0 && <Pill tone="rose">{outOfService} out of service</Pill>}
          {location.shifts.length > 0 && <Pill tone="violet">{plural(location.shifts.length, "shift")}</Pill>}
        </div>
      </PageHeader>

      {error && <Notice tone="rose">{error}</Notice>}

      <EditableCard
        title="Contacts"
        description="Defect reports from the kiosk and machine checklists email the operations manager (or the shift's supervisor). Not Safe reports also copy the general manager."
        className="mb-6"
        {...cardProps("contacts")}
      >
        {editing === "contacts" ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <ContactFields title="Operations manager" prefix="manager" draft={draft} onChange={setField} />
            <ContactFields title="General manager" prefix="general" draft={draft} onChange={setField} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <ContactView
              title="Operations manager"
              name={location.managerName}
              phone={location.managerPhone}
              email={location.managerEmail}
            />
            <ContactView
              title="General manager"
              name={location.generalName}
              phone={location.generalPhone}
              email={location.generalEmail}
            />
          </div>
        )}
      </EditableCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <div className="app-card overflow-hidden rounded-2xl border">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 sm:px-5">
              <div>
                <h2 className="text-lg font-semibold tracking-tight">Forklifts</h2>
                <p className="app-muted text-sm">Click a unit to see its check-ins.</p>
              </div>
              <button
                type="button"
                className="app-btn app-btn-sm app-btn-primary"
                onClick={() => openDialog("add")}
              >
                <AddRoundedIcon sx={{ fontSize: 16 }} /> Add forklift
              </button>
            </div>
            <div className="mt-3 border-t app-divider">
              {forklifts.length === 0 ? (
                <EmptyState
                  compact
                  icon={ForkliftIcon}
                  title={`No forklifts at ${location.location} yet.`}
                  description="Add one, or move a unit here from its own page."
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="app-table">
                    <thead>
                      <tr>
                        <th className="!px-4">Unit</th>
                        <th>Status</th>
                        <th>Last check-in</th>
                        <th className="hidden sm:table-cell">Check-ins ({HISTORY_DAYS}d)</th>
                        <th className="text-right">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {forklifts.map((f) => {
                        const last = lastCheckIn(f);
                        return (
                          <tr
                            key={f.id}
                            tabIndex={0}
                            role="link"
                            aria-label={`Open unit ${f.unit}'s check-ins`}
                            onClick={() => openForklift(f)}
                            // Only the row itself — Enter on an action button
                            // shouldn't also open the unit.
                            onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && openForklift(f)}
                            className="cursor-pointer focus:outline-none focus-visible:bg-[color:var(--app-hover)]"
                          >
                            <td className="!px-4 whitespace-nowrap">
                              <span className="inline-flex items-center gap-2">
                                <Link
                                  to={`/fleet/forklifts/${f.id}`}
                                  state={backState}
                                  onClick={(e) => e.stopPropagation()}
                                  className="font-semibold tabular-nums hover:text-[color:var(--app-accent)]"
                                >
                                  #{f.unit}
                                </Link>
                                {f.moffett && <Pill tone="violet">Moffett</Pill>}
                              </span>
                            </td>
                            <td className="max-w-[14rem]">
                              <ServiceChip inService={f.inService} service={f.service} />
                              {!f.inService && f.service && (
                                <span className="app-muted mt-0.5 block truncate text-xs" title={f.service.note || undefined}>
                                  {serviceSummary(f.service)}
                                </span>
                              )}
                            </td>
                            <td className="whitespace-nowrap">
                              <span className="flex flex-col gap-0.5">
                                <span>
                                  <Pill tone={checkinTone(last)}>{relativeDay(last)}</Pill>
                                </span>
                                {last && (
                                  <span className="app-muted text-xs tabular-nums">
                                    {formatDateTime(last)}
                                    {f.stats.lastInspector ? ` · ${f.stats.lastInspector}` : ""}
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="hidden tabular-nums sm:table-cell">
                              {f.stats.count}
                              {f.stats.flagged > 0 && (
                                <Pill tone="amber" className="ml-2">
                                  {f.stats.flagged} flagged
                                </Pill>
                              )}
                            </td>
                            <td className="whitespace-nowrap text-right">
                              <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="app-icon-btn"
                                  onClick={() => openDialog("edit", f)}
                                  aria-label={`Edit unit ${f.unit}`}
                                  title="Edit unit number, branch, or Moffett"
                                >
                                  <EditRoundedIcon fontSize="small" />
                                </button>
                                <button
                                  type="button"
                                  className={`app-icon-btn ${f.inService ? "" : "text-emerald-600 dark:text-emerald-400"}`}
                                  onClick={() => openDialog("service", f)}
                                  aria-label={f.inService ? `Mark unit ${f.unit} out of service` : `Mark unit ${f.unit} in service`}
                                  title={f.inService ? "Mark out of service" : "Mark in service"}
                                >
                                  <PowerSettingsNewRoundedIcon fontSize="small" />
                                </button>
                                <button
                                  type="button"
                                  className="app-icon-btn text-rose-600 dark:text-rose-400"
                                  onClick={() => openDialog("delete", f)}
                                  aria-label={`Delete unit ${f.unit}`}
                                  title="Delete forklift"
                                >
                                  <DeleteOutlineRoundedIcon fontSize="small" />
                                </button>
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="min-w-0 space-y-6">
          <EditableCard
            title="Shifts"
            description="Only for branches that run more than one shift on the same forklifts. Each shift gets a kiosk button; its supervisor, if set, gets that shift's defect and Not Safe emails."
            {...cardProps("shifts")}
          >
            {editing === "shifts" ? (
              <ShiftsEditor shifts={draft.shifts} onChange={(shifts) => setField("shifts", shifts)} />
            ) : location.shifts.length ? (
              <ul className="space-y-2">
                {location.shifts.map((shift) => (
                  <li key={shift.id} className="app-surface rounded-xl border p-3 text-sm">
                    <p className="font-semibold">{shift.name}</p>
                    <p className="app-muted mt-0.5">
                      {[shift.managerName, shift.managerPhone, shift.managerEmail].filter(Boolean).join(" · ") ||
                        "No supervisor set"}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="app-muted text-sm">One shift — the kiosk shows no shift buttons.</p>
            )}
          </EditableCard>

          <EditableCard title="Alert thresholds" {...cardProps("alerts")}>
            {editing === "alerts" ? (
              <div className="space-y-3">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0 }}
                  label="Days before email alert"
                  value={draft.emailAlert}
                  onChange={(e) => setField("emailAlert", e.target.value)}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0 }}
                  label="Days before text alert"
                  value={draft.textAlert}
                  onChange={(e) => setField("textAlert", e.target.value)}
                />
              </div>
            ) : (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Fact label="Email alert">{location.emailAlert ? plural(Number(location.emailAlert), "day") : ""}</Fact>
                <Fact label="Text alert">{location.textAlert ? plural(Number(location.textAlert), "day") : ""}</Fact>
              </dl>
            )}
          </EditableCard>

          <Card>
            <SectionHeader title="Details" />
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Fact label="Added">{location.dateAdded ? formatDate(location.dateAdded) : ""}</Fact>
              <Fact label="Added by">{location.addedBy}</Fact>
              <Fact label="Last edited">{location.dateUpdated ? formatDate(location.dateUpdated) : ""}</Fact>
              <Fact label="Edited by">{location.updatedBy}</Fact>
            </dl>
          </Card>
        </div>
      </div>
    </PageShell>
  );
};

export default LocationDetail;
